import {
  Box,
  BoxProps,
  HStack,
  SimpleGrid,
  Text,
  VStack,
} from "@chakra-ui/layout";
import Decimal from "decimal.js";
import { add, Dinero, maximum, subtract } from "dinero.js";
import { useTranslation } from "react-i18next";
import { formatMoney, moneyToString, zeroMoney } from "shared/utils/MoneyUtils";

const Column: React.FC<{
  available: Dinero<Decimal>;
  change: Dinero<Decimal>;
  changeLabel: string;
  columnName: string;
  isFundFamily?: boolean;
}> = ({ available, change, columnName, changeLabel, isFundFamily = false }) => {
  const { t } = useTranslation(["common"]);
  // remaining can never be less than 0
  const remaining = maximum([subtract(available, change), zeroMoney()]);
  const moneyFontSize = isFundFamily ? "18px" : "14px";
  const moneyColWidth = isFundFamily ? "150px" : "120px";
  return (
    <VStack alignItems="flex-start">
      <Text textStyle="bodyBold" fontSize="12px">
        {columnName}
      </Text>
      <SimpleGrid
        templateColumns={`1fr ${moneyColWidth}`}
        alignItems="center"
        overflow="hidden"
      >
        <Text textColor="gray.400" fontSize="12px">
          {t("common:available")}
        </Text>
        <Text ml="auto" fontSize={moneyFontSize}>
          {formatMoney(moneyToString(available))}
        </Text>
        <Text textColor="gray.400" fontSize="12px">
          {changeLabel}
        </Text>
        <Text ml="auto" fontSize={moneyFontSize}>
          {formatMoney(moneyToString(change))}
        </Text>
        <Text textColor="gray.400" fontSize="12px">
          {t("common:remaining")}
        </Text>
        <Text ml="auto" fontSize={moneyFontSize}>
          {formatMoney(moneyToString(remaining))}
        </Text>
      </SimpleGrid>
    </VStack>
  );
};

export type FundChangeTableData = {
  [key: string]: {
    available: Dinero<Decimal>;
    change: Dinero<Decimal>;
    fundName: string;
  };
};

export const ChangeTable: React.FC<{
  tableData: FundChangeTableData;
  title: string;
  changeLabel: string;
  boxProps?: BoxProps;
}> = ({ title, tableData, changeLabel, boxProps }) => {
  const { t } = useTranslation(["common"]);
  const fundFamilyTotals = Object.values(tableData).reduce(
    (totals, fundData) => ({
      available: add(totals.available, fundData.available),
      change: add(totals.change, fundData.change),
    }),
    { available: zeroMoney(), change: zeroMoney() },
  );
  return (
    <Box {...boxProps}>
      <Text
        textStyle="small"
        fontWeight={600}
        textColor="gray.400"
        textTransform="uppercase"
        mb="12px"
      >
        {title}
      </Text>
      <HStack gap="24px" flexWrap="wrap">
        {/* we only want to show the fund family totals if there is more than one fund */}
        {Object.keys(tableData).length > 1 && (
          <Column
            available={fundFamilyTotals.available}
            change={fundFamilyTotals.change}
            changeLabel={changeLabel}
            isFundFamily={true}
            columnName={`${t("common:fundFamily")} ${t("common:total")}`}
          />
        )}
        {Object.entries(tableData).map(([fundId, fundData]) => (
          <Column
            key={fundId}
            available={fundData.available}
            change={fundData.change}
            changeLabel={changeLabel}
            columnName={fundData.fundName}
          />
        ))}
      </HStack>
    </Box>
  );
};
